"use client"

import React, { useState, useEffect, useCallback, useRef } from "react"
import { TextField, Autocomplete, CircularProgress, Paper, IconButton, Box, Typography } from "@mui/material"
import { Clear as ClearIcon } from "@mui/icons-material"
import { debounce } from "lodash"
import { fetchSpecialtiesAdaptive } from "./providers-api"

const SpecialtyAutocomplete = ({ value, onChange, error, size = "medium", isPublicRoute = false, ...props }) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  const scrollNodeRef = useRef(null)
  const pendingScrollTop = useRef(null)
  const loadingMoreRef = useRef(loadingMore)
  loadingMoreRef.current = loadingMore

  const loadOptions = useCallback(
    async (searchTerm = "", pageNum = 1, append = false) => {
      if (!hasMore && append) return

      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }
      try {
        const response = await fetchSpecialtiesAdaptive({
          search: searchTerm,
          page: pageNum,
          limit: 20,
        }, isPublicRoute)

        const newOptions = response.specialties || []
        setOptions((prev) => (append ? [...prev, ...newOptions] : newOptions))
        setHasMore(response.hasMore || false)
        setPage(pageNum)
      } catch (error) {
        console.error("Error loading specialties:", error)
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [hasMore],
  )

  useEffect(() => {
    if (pendingScrollTop.current != null && scrollNodeRef.current) {
      scrollNodeRef.current.scrollTop = pendingScrollTop.current
      pendingScrollTop.current = null
    }
  }, [options])

  const debouncedLoadOptions = useCallback(
    debounce((searchTerm) => {
      setPage(1)
      setHasMore(true)
      loadOptions(searchTerm, 1, false)
    }, 1200), // Increased to 1.2 seconds
    [loadOptions],
  )

  useEffect(() => {
    loadOptions(value?.name || "", 1, false)
  }, [])

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'reset' && newInputValue === '' && !value && inputValue !== '') {
      return
    }

    setInputValue(newInputValue)

    // Don't trigger API call when user selects an option from dropdown
    // reason can be: 'input' (user typing), 'reset' (selection/clear), 'clear' (clear button)
    if (reason === 'reset' || reason === 'clear') {
      return
    }

    // Only trigger API call when user is actually typing
    if (reason === 'input') {
      debouncedLoadOptions(newInputValue)
    }
  }

  const handleChange = (event, newValue) => {
    onChange(newValue)
  }

  const handleClear = () => {
    setInputValue("")
    onChange(null)
    setPage(1)
    setHasMore(true)
    loadOptions("", 1, false)
  }

  const handleScroll = (event) => {
    const { target } = event
    scrollNodeRef.current = target
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 5 && hasMore && !loading && !loadingMore) {
      pendingScrollTop.current = target.scrollTop
      loadOptions(inputValue, page + 1, true)
    }
  }

  const renderPaper = useCallback((paperProps) => (
    <Paper
      {...paperProps}
      sx={{
        border: '1px solid #ccc',
        borderRadius: 2,
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
      }}
    >
      {paperProps.children}
      {loadingMoreRef.current && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1 }}>
          <CircularProgress size={14} />
          <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>Loading more...</Typography>
        </Box>
      )}
    </Paper>
  ), [])

  return (
    <Autocomplete
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={(option) => option?.name || ""}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      loading={loading}
      size={size}
      disableClearable
      PaperComponent={renderPaper}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Select specialty"
          variant="outlined"
          fullWidth
          error={error}
          size={size}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1.5,
              fontSize: "0.8rem",
              "&.Mui-focused": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#035fcb",
                  borderWidth: 1,
                },
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#9ca3af",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d1d5db",
              },
              ...(error && {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d32f2f",
                  borderWidth: 1,
                },
              }),
            },
            "& .MuiInputBase-input": {
              fontSize: "0.8rem",
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={16} />}
                {!loading && inputValue && (
                  <IconButton
                    size="small"
                    aria-label="Clear specialty"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleClear}
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      ListboxProps={{
        onScroll: handleScroll,
        style: { maxHeight: 150, fontSize: "0.8rem" },
      }}
      noOptionsText={inputValue ? "No specialties found" : "Start typing to search"}
      {...props}
    />
  )
}

export default SpecialtyAutocomplete